import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../../store/actions';

export const useFetch = ({
  url,
  method = 'GET',
  params,
  body,
  skip = false,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Store callbacks in refs
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Serialize params and body to detect real changes
  const paramsString = params ? JSON.stringify(params) : '';
  const bodyString = body ? JSON.stringify(body) : '';

  const fetchData = useCallback(
    async (overrideBody) => {
      if (skip) return;

      dispatch(setLoading(true));
      setError(null);

      try {
        // Parse params back from string
        const parsedParams = paramsString ? JSON.parse(paramsString) : null;
        const parsedBody = bodyString ? JSON.parse(bodyString) : null;

        const queryString = parsedParams
          ? `?${new URLSearchParams(parsedParams).toString()}`
          : '';

        const requestBody = overrideBody || parsedBody;

        const response = await fetch(`${url}${queryString}`, {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: requestBody ? JSON.stringify(requestBody) : undefined,
        });

        if (!response.ok) {
          let errData = {};
          try {
            errData = await response.json();
          } catch (_) { }

          throw new Error(
            errData.message || `Request failed with status ${response.status}`
          );
        }

        const result = await response.json();
        setData(result);

        if (onSuccessRef.current) {
          onSuccessRef.current(result);
        }
      } catch (err) {
        setError(err);
        if (onErrorRef.current) {
          onErrorRef.current(err);
        }
      } finally {
        dispatch(setLoading(false));
      }
    },
    [url, method, paramsString, bodyString, skip, dispatch]
  );

  useEffect(() => {
    if (!skip) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, fetchData]);

  useEffect(() => {
    return () => {
      dispatch(setLoading(false));
    };
  }, [dispatch]);

  return {
    data,
    error,
    fetchData,
    setData,
  };
};